import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import EnterpriseOperationalSection from '../enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import {
  fetchScannerProviders,
  fetchScannerScans,
  fetchScanFindings,
  patchScannerProvider,
  testScannerProvider,
  runAssetLinkageDiagnostics,
  runGreenboneReportDiagnostics,
  type GreenboneReportDiagnostics,
  type ScannerProviderRow,
  type ScannerScanRow,
  type ScannerFindingRow,
  type AssetLinkageDiagnostics,
} from '../../services/scannerGatewayService'

type Props = { token: string }

const STATUS_CLASS: Record<string, string> = {
  AVAILABLE: entStyles.badgeSuccess || '',
  DISCONNECTED: entStyles.badgeWarning || '',
  DISABLED: entStyles.badgeMuted || '',
  MISCONFIGURED: entStyles.badgeWarning || '',
  ERROR: entStyles.badgeDanger || '',
}

function statusBadgeClass(status: string) {
  return STATUS_CLASS[status] || ''
}

export default function ScannerGatewayPanel({ token }: Props) {
  const { t } = useI18n()
  const { scanner } = useParams()
  const focus = scanner === 'greenbone' || scanner === 'zap' || scanner === 'nuclei' ? scanner : ''
  const [providers, setProviders] = useState<ScannerProviderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [zapEndpoint, setZapEndpoint] = useState('http://127.0.0.1:8080')
  const [zapApiKey, setZapApiKey] = useState('')
  const [gbPassword, setGbPassword] = useState('')
  const [gbUsername, setGbUsername] = useState('admin')
  const [diagAssetId, setDiagAssetId] = useState('')
  const [diagResult, setDiagResult] = useState<AssetLinkageDiagnostics | null>(null)
  const [diagError, setDiagError] = useState<string | null>(null)
  const [gbScanId, setGbScanId] = useState('')
  const [gbDiagResult, setGbDiagResult] = useState<GreenboneReportDiagnostics | null>(null)
  const [gbDiagError, setGbDiagError] = useState<string | null>(null)
  const [historyProviderId, setHistoryProviderId] = useState<string | null>(null)
  const [scans, setScans] = useState<ScannerScanRow[]>([])
  const [findingsScanId, setFindingsScanId] = useState<string | null>(null)
  const [findings, setFindings] = useState<ScannerFindingRow[]>([])

  const requestRef = useRef(0)
  const load = useCallback(async () => {
    const requestId = requestRef.current + 1
    requestRef.current = requestId
    setLoading(true)
    setError(null)
    setProviders([])
    setScans([])
    setFindings([])
    try {
      const rows = await fetchScannerProviders(token)
      if (requestId !== requestRef.current) return
      setProviders(rows)
      const zap = rows.find((p) => p.providerType === 'OWASP_ZAP')
      if (zap?.endpointUrl) setZapEndpoint(zap.endpointUrl)
    } catch (e) {
      if (requestId !== requestRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const onToggle = async (p: ScannerProviderRow, enabled: boolean) => {
    setBusyId(p.id)
    try {
      const updated = await patchScannerProvider(token, p.id, { enabled })
      setProviders((prev) => prev.map((row) => (row.id === p.id ? updated : row)))
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Update failed')
    } finally {
      setBusyId(null)
    }
  }

  const onTest = async (p: ScannerProviderRow) => {
    setBusyId(p.id)
    try {
      const updated = await testScannerProvider(token, p.id)
      setProviders((prev) => prev.map((row) => (row.id === p.id ? updated : row)))
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Test failed')
    } finally {
      setBusyId(null)
    }
  }

  const saveZapConfig = async (p: ScannerProviderRow) => {
    setBusyId(p.id)
    try {
      const body: { endpointUrl: string; apiKey?: string } = { endpointUrl: zapEndpoint.trim() }
      if (zapApiKey.trim()) body.apiKey = zapApiKey.trim()
      const updated = await patchScannerProvider(token, p.id, body)
      setProviders((prev) => prev.map((row) => (row.id === p.id ? updated : row)))
      setZapApiKey('')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusyId(null)
    }
  }

  const saveGreenboneCredentials = async (p: ScannerProviderRow) => {
    if (!gbPassword.trim()) {
      alert(
        t(
          'integrations.scannerGateway.gbPasswordRequired',
          'Enter the Greenbone GVM password to re-encrypt and save with the current key.',
        ),
      )
      return
    }
    setBusyId(p.id)
    try {
      const updated = await patchScannerProvider(token, p.id, {
        apiKey: gbPassword.trim(),
        username: gbUsername.trim() || 'admin',
        authType: 'password',
      })
      setProviders((prev) => prev.map((row) => (row.id === p.id ? updated : row)))
      setGbPassword('')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusyId(null)
    }
  }

  const runDiagnostics = async () => {
    const id = diagAssetId.trim()
    if (!id) {
      setDiagError(t('integrations.scannerGateway.diagAssetRequired', 'Enter an asset ID'))
      return
    }
    setDiagError(null)
    setDiagResult(null)
    setBusyId('diag')
    try {
      const data = await runAssetLinkageDiagnostics(token, id)
      setDiagResult(data)
    } catch (e) {
      setDiagError(e instanceof Error ? e.message : 'Diagnostics failed')
    } finally {
      setBusyId(null)
    }
  }

  const runGreenboneDiag = async () => {
    const id = gbScanId.trim()
    if (!id) {
      setGbDiagError(t('integrations.scannerGateway.scanIdRequired', 'Enter a scan ID'))
      return
    }
    setGbDiagError(null)
    setGbDiagResult(null)
    setBusyId('gb-diag')
    try {
      const data = await runGreenboneReportDiagnostics(token, id)
      setGbDiagResult(data)
    } catch (e) {
      setGbDiagError(e instanceof Error ? e.message : 'Report diagnostics failed')
    } finally {
      setBusyId(null)
    }
  }

  const openHistory = async (p: ScannerProviderRow) => {
    setHistoryProviderId(p.id)
    setFindingsScanId(null)
    setFindings([])
    try {
      const rows = await fetchScannerScans(token, p.id)
      setScans(rows)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to load scan history')
      setHistoryProviderId(null)
    }
  }

  const openFindings = async (scanId: string) => {
    setFindingsScanId(scanId)
    try {
      const data = await fetchScanFindings(token, scanId)
      setFindings(data.findings || [])
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to load findings')
      setFindings([])
    }
  }

  useEffect(() => {
    setZapApiKey('')
    setGbPassword('')
  }, [token])

  const zapProvider = providers.find((p) => p.providerType === 'OWASP_ZAP')
  const greenboneProvider = providers.find((p) => p.providerType === 'GREENBONE')

  return (
    <EnterpriseOperationalSection
      layerLabel={t('integrations.scannerGateway.layer', 'Scanner Gateway')}
      title={t('integrations.scannerGateway.title', 'Scanner Gateway')}
      subtitle={t(
        'integrations.scannerGateway.subtitle',
        'Unified scanner providers: Greenbone, OWASP ZAP, and Nuclei.',
      )}
      testId="integrations-scanner-gateway"
    >
      <nav aria-label={t('integrations.scannerGateway.settingsNav', 'Scanner settings')}>
        <NavLink end to="/integrations/scanners">{t('integrations.scannerGateway.all', 'All scanners')}</NavLink>
        {' · '}
        <NavLink to="/integrations/scanners/greenbone">Greenbone</NavLink>
        {' · '}
        <NavLink to="/integrations/scanners/zap">OWASP ZAP</NavLink>
        {' · '}
        <NavLink to="/integrations/scanners/nuclei">Nuclei</NavLink>
      </nav>
      {error && (
        <p className={entStyles.metaRow} style={{ color: 'var(--color-danger, #b91c1c)' }}>
          {error}
        </p>
      )}
      {loading && <p className={entStyles.metaRow}>{t('common.loading', 'Loading…')}</p>}
      <div className={entStyles.tableWrap}>
        <table className={entStyles.dataTable}>
          <thead>
            <tr>
              <th>{t('integrations.scannerGateway.enabled', 'Enabled')}</th>
              <th>{t('integrations.scannerGateway.scanner', 'Scanner')}</th>
              <th>{t('integrations.scannerGateway.status', 'Status')}</th>
              <th>{t('integrations.scannerGateway.lifecycle', 'Lifecycle')}</th>
              <th>{t('integrations.scannerGateway.endpoint', 'Endpoint / mode')}</th>
              <th>{t('integrations.scannerGateway.lastHealth', 'Last health check')}</th>
              <th>{t('integrations.scannerGateway.lastScan', 'Last scan')}</th>
              <th>{t('integrations.scannerGateway.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => {
              const life = p.lifecycle
              return (
              <tr key={p.id} data-testid={`scanner-provider-${p.providerType}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={p.enabled}
                    disabled={busyId === p.id}
                    onChange={(e) => void onToggle(p, e.target.checked)}
                    aria-label={`Enable ${p.displayName}`}
                  />
                </td>
                <td>
                  <strong>{p.displayName}</strong>
                  <div className={entStyles.metaRow}>{p.providerType}</div>
                </td>
                <td>
                  <span className={statusBadgeClass(p.status)}>{p.status}</span>
                  {p.statusMessage && (
                    <div className={entStyles.metaRow} title={p.statusMessage}>
                      {p.statusMessage}
                    </div>
                  )}
                </td>
                <td>
                  <div className={entStyles.metaRow} data-testid={`scanner-lifecycle-${p.providerType}`}>
                    {life?.supported ? 'supported' : 'unsupported'}
                    {' · '}
                    {life?.configured ? 'configured' : 'not configured'}
                    {' · '}
                    {life?.enabled ? 'enabled' : 'disabled'}
                    {' · '}
                    {life?.healthy ? 'healthy' : 'not healthy'}
                    {' · '}
                    {life?.launchable ? 'launchable' : 'not launchable'}
                  </div>
                  {(life?.nextAction || life?.disabledReason) && (
                    <div className={entStyles.metaRow} title={life?.nextAction || life?.disabledReason || ''}>
                      {life?.nextAction || life?.disabledReason}
                    </div>
                  )}
                </td>
                <td>{p.endpointUrl || (p.configJson as { mode?: string })?.mode || '—'}</td>
                <td>
                  {p.lastHealthCheckAt ? new Date(p.lastHealthCheckAt).toLocaleString() : '—'}
                </td>
                <td>{p.lastScanAt ? new Date(p.lastScanAt).toLocaleString() : '—'}</td>
                <td>
                  <div className={entStyles.formActions}>
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      disabled={busyId === p.id}
                      onClick={() => void onTest(p)}
                    >
                      {t('integrations.scannerGateway.test', 'Test connection')}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      onClick={() => void openHistory(p)}
                    >
                      {t('integrations.scannerGateway.history', 'View scan history')}
                    </Button>
                  </div>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {greenboneProvider && (focus === '' || focus === 'greenbone') && (
        <form
          id="scanner-settings-greenbone"
          className={entStyles.cardBlock}
          style={{ marginTop: 16 }}
          data-testid="greenbone-credentials-form"
          onSubmit={(e) => {
            e.preventDefault()
            void saveGreenboneCredentials(greenboneProvider)
          }}
        >
          <h4 className={entStyles.sectionTitle}>
            {t('integrations.scannerGateway.gbConfig', 'Greenbone / OpenVAS credentials')}
          </h4>
          <p className={entStyles.metaRow}>
            {t(
              'integrations.scannerGateway.gbConfigHint',
              'If status shows CREDENTIALS_DECRYPT_FAILED, re-enter the GVM password here. It is encrypted with the current ENCRYPTION_KEY and written to gvm.config.json. Do not change ENCRYPTION_KEY.',
            )}
          </p>
          <div className={entStyles.formGrid}>
            <label>
              {t('integrations.scannerGateway.gbUsername', 'GVM username')}
              <input
                type="text"
                value={gbUsername}
                onChange={(e) => setGbUsername(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label>
              {t('integrations.scannerGateway.gbPassword', 'GVM password')}
              <input
                type="password"
                value={gbPassword}
                onChange={(e) => setGbPassword(e.target.value)}
                placeholder={t(
                  'integrations.scannerGateway.gbPasswordPlaceholder',
                  'Enter password to re-encrypt and save',
                )}
                autoComplete="new-password"
              />
            </label>
          </div>
          <div className={entStyles.formActions}>
            <Button
              type="submit"
              size="small"
              color="primary"
              disabled={busyId === greenboneProvider.id}
            >
              {t('integrations.scannerGateway.saveGb', 'Save Greenbone password')}
            </Button>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={busyId === greenboneProvider.id}
              onClick={() => void onTest(greenboneProvider)}
            >
              {t('integrations.scannerGateway.test', 'Test connection')}
            </Button>
          </div>
        </form>
      )}

      {zapProvider && (focus === '' || focus === 'zap') && (
        <form
          id="scanner-settings-zap"
          className={entStyles.cardBlock}
          style={{ marginTop: 16 }}
          onSubmit={(e) => {
            e.preventDefault()
            void saveZapConfig(zapProvider)
          }}
        >
          <h4 className={entStyles.sectionTitle}>
            {t('integrations.scannerGateway.zapConfig', 'OWASP ZAP configuration')}
          </h4>
          <div className={entStyles.formGrid}>
            <label>
              {t('integrations.scannerGateway.endpoint', 'Endpoint URL')}
              <input
                type="url"
                value={zapEndpoint}
                onChange={(e) => setZapEndpoint(e.target.value)}
                placeholder="http://127.0.0.1:8080"
              />
            </label>
            <label>
              {t('integrations.scannerGateway.apiKey', 'API key')}
              <input
                type="password"
                value={zapApiKey}
                onChange={(e) => setZapApiKey(e.target.value)}
                placeholder={t('integrations.scannerGateway.apiKeyPlaceholder', 'Leave blank to keep existing')}
                autoComplete="off"
              />
            </label>
          </div>
          <div className={entStyles.formActions}>
            <Button type="submit" size="small" color="primary" disabled={busyId === zapProvider.id}>
              {t('integrations.scannerGateway.saveZap', 'Save ZAP settings')}
            </Button>
          </div>
        </form>
      )}

      {(focus === '' || focus === 'nuclei') && (
        <p id="scanner-settings-nuclei" className={entStyles.metaRow}>
          {t(
            'integrations.scannerGateway.nucleiSettings',
            'Nuclei uses the provider row above. There is no separate credential form.',
          )}
        </p>
      )}

      <div className={entStyles.cardBlock} style={{ marginTop: 16 }}>
        <h4 className={entStyles.sectionTitle}>
          {t('integrations.scannerGateway.diagnostics', 'Diagnostics')}
        </h4>
        <p className={entStyles.metaRow}>
          {t(
            'integrations.scannerGateway.diagnosticsHint',
            'Verify asset → information system → enabled standards linkage before launching scans.',
          )}
        </p>
        <div className={entStyles.formActions}>
          <input
            type="text"
            value={diagAssetId}
            onChange={(e) => setDiagAssetId(e.target.value)}
            placeholder={t('integrations.scannerGateway.assetId', 'Asset ID (integer)')}
            aria-label={t('integrations.scannerGateway.assetId', 'Asset ID')}
          />
          <Button
            type="button"
            size="small"
            color="outline"
            disabled={busyId === 'diag'}
            onClick={() => void runDiagnostics()}
          >
            {t('integrations.scannerGateway.runAssetLinkage', 'Run asset linkage diagnostics')}
          </Button>
        </div>
        {diagError && (
          <p className={entStyles.metaRow} style={{ color: 'var(--color-danger, #b91c1c)' }}>
            {diagError}
          </p>
        )}
        {diagResult && (
          <pre className={entStyles.codeBlock} style={{ marginTop: 8, fontSize: 12 }}>
            {JSON.stringify(diagResult, null, 2)}
          </pre>
        )}
        <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid var(--ds-border,#e5e7eb)' }} />
        <p className={entStyles.metaRow}>
          {t(
            'integrations.scannerGateway.greenboneReportDiagHint',
            'Greenbone PDF/XML report retrieval diagnostics (ScanHistory scan ID).',
          )}
        </p>
        <div className={entStyles.formActions}>
          <input
            type="text"
            value={gbScanId}
            onChange={(e) => setGbScanId(e.target.value)}
            placeholder={t('integrations.scannerGateway.scanHistoryId', 'Scan ID (ScanHistory)')}
            aria-label={t('integrations.scannerGateway.scanHistoryId', 'Scan ID')}
          />
          <Button
            type="button"
            size="small"
            color="outline"
            disabled={busyId === 'gb-diag'}
            onClick={() => void runGreenboneDiag()}
          >
            {t('integrations.scannerGateway.runGreenboneReportDiag', 'Run Greenbone report diagnostics')}
          </Button>
        </div>
        {gbDiagError && (
          <p className={entStyles.metaRow} style={{ color: 'var(--color-danger, #b91c1c)' }}>
            {gbDiagError}
          </p>
        )}
        {gbDiagResult && (
          <pre className={entStyles.codeBlock} style={{ marginTop: 8, fontSize: 12 }}>
            {JSON.stringify(gbDiagResult, null, 2)}
          </pre>
        )}
      </div>

      {historyProviderId && (
        <div className={entStyles.cardBlock} style={{ marginTop: 16 }}>
          <div className={entStyles.formActions}>
            <h4 className={entStyles.sectionTitle}>
              {t('integrations.scannerGateway.scanHistory', 'Scan history')}
            </h4>
            <Button type="button" size="small" color="outline" onClick={() => setHistoryProviderId(null)}>
              {t('common.close', 'Close')}
            </Button>
          </div>
          <div className={entStyles.tableWrap}>
            <table className={entStyles.dataTable}>
              <thead>
                <tr>
                  <th>{t('integrations.scannerGateway.target', 'Target')}</th>
                  <th>{t('integrations.scannerGateway.scanType', 'Type')}</th>
                  <th>{t('integrations.scannerGateway.status', 'Status')}</th>
                  <th>{t('integrations.scannerGateway.created', 'Created')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {scans.map((s) => (
                  <tr key={s.id}>
                    <td>{s.target}</td>
                    <td>{s.scanType}</td>
                    <td>{s.status}</td>
                    <td>{new Date(s.createdAt).toLocaleString()}</td>
                    <td>
                      <Button type="button" size="small" color="outline" onClick={() => void openFindings(s.id)}>
                        {t('integrations.scannerGateway.findings', 'Findings')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {findingsScanId && (
            <div style={{ marginTop: 12 }}>
              <h5>{t('integrations.scannerGateway.findingsFor', 'Findings')} ({findings.length})</h5>
              <ul className={entStyles.metaRow}>
                {findings.map((f) => (
                  <li key={f.id}>
                    <strong>[{f.severity}]</strong> {f.title}
                    {f.cwe ? ` (${f.cwe})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className={entStyles.formActions} style={{ marginTop: 12 }}>
        <Button type="button" size="small" color="outline" onClick={() => void load()}>
          {t('common.refresh', 'Refresh')}
        </Button>
      </div>
    </EnterpriseOperationalSection>
  )
}
